import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPlaylists, triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import { track as trackEvent } from '../utils/analytics';
import { localPathFromTrack } from '../utils/cloudPlaylistTracks';
import { DEFAULT_PLAYLIST_COVER, playlistCoverUrl } from '../utils/cover';
import { refreshAndRetry } from '../redux/utils/refreshAndRetry';
import { openPlaylist, isMobileLayout } from '../utils/openInLibrary';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { useIsMobileWeb } from '../hooks/useIsMobileWeb';
import MobileListSearch, { matchesQuery } from './common/MobileListSearch';
import { registerUserScopedReset } from '../utils/userScopedCaches';
import './AddToPlaylistModal.scss';

const isDesktop = typeof window !== 'undefined' && window.electron?.db;
// Phone layout: the sheet is CSS-driven (AddToPlaylistModal.scss, ≤768px),
// this only gates behaviour — no autofocus (the keyboard would cover the
// list), rows are tap targets, and the filter appears only for long lists.
const isMobile = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches;

// Last successfully loaded playlist list per source ('local' | 'server') —
// module-scoped so opening the modal paints the previous list instantly
// (stale-while-revalidate) instead of an empty spinner, while the fresh
// fetch replaces it in place. Only the very first open per session waits.
const _lastLoaded = new Map();
// Those lists are the ACCOUNT's playlists — without this, the first open
// after an account switch painted the previous user's playlist names.
registerUserScopedReset(() => _lastLoaded.clear());

// Bulk mode: pass `bulkFilePaths` (an array of local file paths) + `defaultName`
// + `bulkLabel` to create/add many local tracks at once (e.g. a whole folder),
// and `onCreated(playlistId, name)` to react after a new playlist is made.
// `initialTab` ('add' | 'create') overrides the default tab — bulk mode
// defaults to 'create' (the folder→playlist flow), but the Library tab's
// context-menu add wants 'add' first.
const AddToPlaylistModal = ({ show, onHide, track, bulkFilePaths, defaultName, bulkLabel, onCreated, initialTab }) => {
    const isBulk = Array.isArray(bulkFilePaths) && bulkFilePaths.length > 0;
    const dispatch = useDispatch();
    const toast = useToast();
    const accessToken = useSelector(state => state.auth.accessToken);
    const useServer = !!accessToken;
    // Which backing list this open reads — must mirror loadPlaylists' branching.
    const sourceKey = (isBulk && isDesktop) || !useServer ? 'local' : 'server';
    const initialCached = _lastLoaded.get(sourceKey);

    const [activeTab, setActiveTab] = useState('add'); // 'add' | 'create'
    const [playlists, setPlaylistsLocal] = useState(() => initialCached || []);
    const [loading, setLoading] = useState(!initialCached);
    const [filter, setFilter] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
    const [coverImage, setCoverImage] = useState(null); // data URL
    const [creating, setCreating] = useState(false);
    const [addingToPlaylistId, setAddingToPlaylistId] = useState(null);
    const overlayRef = useRef(null);
    const coverInputRef = useRef(null);

    const loadPlaylists = useCallback(async () => {
        try {
            // Stale-while-revalidate: when a previous load exists, paint it
            // immediately and refresh silently; spinner only on a cold cache.
            const cached = _lastLoaded.get(sourceKey);
            if (cached) {
                setPlaylistsLocal(cached);
                setLoading(false);
            } else {
                setLoading(true);
            }
            if (isBulk && isDesktop) {
                // Bulk adds write local file paths to LOCAL playlists
                // (db.playlists.addTracks) — list those even when logged in;
                // cloud playlist ids don't resolve in the local DB.
                const result = await window.electron.db.playlists.getAll(false);
                const list = result || [];
                setPlaylistsLocal(list);
                _lastLoaded.set('local', list);
            } else if (useServer) {
                // refreshAndRetry: stale-token 401 refreshes + retries instead
                // of rendering an empty modal (reopening also refetches).
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                if (result?.error) {
                    toast.error('Failed to load playlists');
                    return;
                }
                const normalized = (result?.data || []).map(p => ({ ...p, trackCount: p.track_count }));
                setPlaylistsLocal(normalized);
                _lastLoaded.set('server', normalized);
            } else if (isDesktop) {
                const result = await window.electron.db.playlists.getAll(false);
                const list = result || [];
                setPlaylistsLocal(list);
                _lastLoaded.set('local', list);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setLoading(false);
        }
    }, [toast, useServer, isBulk, sourceKey]);

    useEffect(() => {
        if (show) {
            loadPlaylists();
            // Folder/bulk create defaults straight to the "Create" tab, pre-named.
            setActiveTab(initialTab || (isBulk ? 'create' : 'add'));
            setNewPlaylistName(isBulk ? (defaultName || '') : '');
            setNewPlaylistDescription('');
            setCoverImage(null);
        }
    }, [show, loadPlaylists, isBulk, defaultName, initialTab]);

    // Same constraints as the sidebar's playlist form: image files only, ≤2MB,
    // stored as a data URL in cover_image.
    const handleCoverFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.warning('Image size should be less than 2MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            toast.warning('Please select an image file');
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => setCoverImage(event.target.result);
        reader.readAsDataURL(file);
    };

    // Close on Escape
    useEffect(() => {
        if (!show) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') onHide();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [show, onHide]);

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) {
            onHide();
        }
    };

    const buildOnlineTrack = () => {
        return {
            title: track.title || 'Unknown',
            artist: track.artist || '',
            // music-info public id — lets cloud playlists resolve recommendations
            // by id instead of re-matching "Artist - Title" text.
            publicId: track.publicId || track.public_id || track.track_public_id || null,
            // Mix identity (MixContextMenu adds) — separate from publicId,
            // which strictly means a TRACK catalog id. Marks the playlist
            // row as a mix so clients open it id-first as a mix.
            mixPublicId: track.mixPublicId || null,
            artwork: track.artwork || track.artworkUrl || track.image || '',
            previewUrl: track.previewUrl || track.sampleUrl || track.preview_url || track.preview || '',
            // Play links: without these, a sample-less track lands in the cloud
            // playlist with no source and renders as "not on this device".
            youtubeId: (track.youtubeId && track.youtubeId !== '-1' ? track.youtubeId : track.youtube_id) || null,
            soundcloudUrl: track.soundcloudUrl || track.soundcloud_url || null,
            bpm: track.bpm || null,
            key: track.key || null,
            genre: track.genre || null,
            genres: track.genres || null,
            styles: track.styles || null,
            releaseDate: track.releaseDate || null,
            label: track.label || null,
        };
    };

    const getOnlinePath = () => {
        // Local-file tracks (the Manage/Prepare tabs) carry the real on-disk
        // path so the desktop playlist entry maps back to the actual file
        // instead of an `online://` stub that can't be played locally.
        if (track.filePath) return track.filePath;
        const previewUrl = track.previewUrl || track.sampleUrl || track.preview_url || track.preview || '';
        if (previewUrl) return previewUrl;
        return `online://${track.artist || 'unknown'} - ${track.title || 'unknown'}`;
    };

    // "<Track> was added to <Playlist>." card (same shape as the like toast);
    // the playlist name is the link and opens that playlist. Local-file tracks
    // without catalog art fall back to the embedded cover via the audio server.
    const showAddedCard = (playlistId, playlistName) => {
        const localPath = track?.filePath || null;
        toast.card({
            artwork: track?.artwork || track?.artworkUrl || track?.image || track?.entityArtwork
                || (localPath && isDesktop ? `http://localhost:3069/album-art?path=${encodeURIComponent(localPath)}` : null),
            title: track?.title || 'Track',
            message: 'was added to',
            duration: isMobileLayout() ? 2800 : 3500,
            link: {
                label: playlistName || 'playlist',
                onClick: () => {
                    const layout = openPlaylist(dispatch, playlistId);
                    trackEvent('playlist_opened', { from: 'add_to_playlist_toast', layout });
                },
            },
        });
    };

    const handleAddToPlaylist = async (playlistId) => {
        const playlistName = playlists.find(p => p.id === playlistId)?.name;
        try {
            setAddingToPlaylistId(playlistId);
            if (isBulk && isDesktop) {
                const result = await window.electron.db.playlists.addTracks(playlistId, bulkFilePaths);
                const n = result?.added ?? bulkFilePaths.length;
                toast.success(`Added ${n} track${n !== 1 ? 's' : ''} to playlist`);
                dispatch(triggerPlaylistRefresh());
                if (onCreated) onCreated(playlistId);
                onHide();
                return;
            }
            if (useServer) {
                const onlineTrack = buildOnlineTrack();
                const localPath = localPathFromTrack(track);
                const result = await refreshAndRetry((token) => window.electron.library.addTrackToPlaylist(token, playlistId, {
                    artist: onlineTrack.artist,
                    title: onlineTrack.title,
                    trackPublicId: onlineTrack.publicId,
                    mixPublicId: onlineTrack.mixPublicId,
                    artwork: onlineTrack.artwork,
                    preview_url: onlineTrack.previewUrl,
                    youtube_id: onlineTrack.youtubeId,
                    soundcloud_url: onlineTrack.soundcloudUrl,
                    file_path: localPath || '',
                }));
                // Seed the cloud↔local link (row id returned on create AND
                // duplicate) so the first playlist load resolves exactly.
                const rowId = result?.data?.id || result?.data?.track?.id;
                if (localPath && rowId) {
                    window.electron.db?.cloudLinks?.setByPath?.(rowId, localPath)?.catch?.(() => {});
                }
                if (result?.error) {
                    toast.error(result.error);
                } else if (result?.data?.alreadyExists || result?.alreadyExists) {
                    toast.info('Track already in playlist');
                } else {
                    showAddedCard(playlistId, playlistName);
                    dispatch(triggerPlaylistRefresh());
                    onHide();
                }
            } else if (isDesktop) {
                const onlinePath = getOnlinePath();
                const result = await window.electron.db.playlists.addTracks(playlistId, [onlinePath]);

                if (result && result.added > 0) {
                    showAddedCard(playlistId, playlistName);
                    dispatch(triggerPlaylistRefresh());
                    onHide();
                } else if (result && result.failed > 0) {
                    const onlineTrack = buildOnlineTrack();
                    const tempName = `__temp_add_${Date.now()}`;
                    const tempPlaylist = await window.electron.db.playlists.createFromOnlineTracks(
                        tempName,
                        [onlineTrack]
                    );

                    const retryResult = await window.electron.db.playlists.addTracks(playlistId, [onlinePath]);

                    if (tempPlaylist?.id) {
                        try { await window.electron.db.playlists.delete(tempPlaylist.id); } catch (e) {}
                    }

                    if (retryResult && retryResult.added > 0) {
                        showAddedCard(playlistId, playlistName);
                        dispatch(triggerPlaylistRefresh());
                        onHide();
                    } else {
                        toast.info('Track already in playlist');
                    }

                    const allPlaylists = await window.electron.db.playlists.getAll(false);
                    dispatch(setPlaylists(allPlaylists || []));
                } else {
                    toast.info('Track already in playlist');
                }
            }
        } catch (error) {
            console.error('Error adding track to playlist:', error);
            toast.error('Failed to add track to playlist');
        } finally {
            setAddingToPlaylistId(null);
        }
    };

    const handleCreatePlaylist = async (e) => {
        e.preventDefault();
        if (!newPlaylistName.trim()) {
            toast.warning('Please enter a playlist name');
            return;
        }

        try {
            setCreating(true);
            if (isBulk && isDesktop) {
                const playlist = await window.electron.db.playlists.create(newPlaylistName.trim(), newPlaylistDescription.trim() || null);
                const pid = playlist?.id ?? playlist;
                if (pid != null) {
                    if (coverImage) {
                        // create() takes no cover — set it via update, like the
                        // sidebar's create form does.
                        await window.electron.db.playlists.update(pid, {
                            name: newPlaylistName.trim(),
                            description: newPlaylistDescription.trim() || null,
                            coverImage,
                        });
                    }
                    await window.electron.db.playlists.addTracks(pid, bulkFilePaths);
                    // When logged in, the sidebar reads CLOUD playlists — don't inject
                    // local-id playlists here (they wouldn't resolve on the server).
                    // triggerPlaylistRefresh reloads the correct source. Only seed the
                    // local list when offline (getAll TRUE so tracks are included).
                    if (!useServer) {
                        const allPlaylists = await window.electron.db.playlists.getAll(true);
                        dispatch(setPlaylists(allPlaylists || []));
                    }
                    // New playlist → the cached list is stale; drop it so the
                    // next open fetches fresh instead of painting without it.
                    _lastLoaded.delete('local');
                    dispatch(triggerPlaylistRefresh());
                    toast.success(`Created playlist "${newPlaylistName.trim()}"`);
                    trackEvent('playlist_created', { track_count: bulkFilePaths.length, from: 'add_to_playlist_modal' });
                    if (onCreated) onCreated(pid, newPlaylistName.trim());
                    onHide();
                } else {
                    toast.error('Failed to create playlist');
                }
                return;
            }
            const onlineTrack = buildOnlineTrack();
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.createPlaylist(token, {
                    name: newPlaylistName.trim(),
                    description: newPlaylistDescription.trim() || null,
                    cover_image: coverImage || onlineTrack.artwork || null,
                }));
                if (result?.error) {
                    toast.error(typeof result.error === 'string' ? result.error : 'Failed to create playlist');
                    return;
                }
                const playlist = result?.data || result;
                if (playlist?.id) {
                    const localPath = localPathFromTrack(track);
                    const addResult = await refreshAndRetry((token) => window.electron.library.addTrackToPlaylist(token, playlist.id, {
                        artist: onlineTrack.artist,
                        title: onlineTrack.title,
                        trackPublicId: onlineTrack.publicId,
                        mixPublicId: onlineTrack.mixPublicId,
                        artwork: onlineTrack.artwork,
                        preview_url: onlineTrack.previewUrl,
                        youtube_id: onlineTrack.youtubeId,
                        soundcloud_url: onlineTrack.soundcloudUrl,
                        file_path: localPath || '',
                    }));
                    if (addResult?.error) console.error('Failed to add track:', addResult.error);
                    const rowId = addResult?.data?.id || addResult?.data?.track?.id;
                    if (localPath && rowId) {
                        window.electron.db?.cloudLinks?.setByPath?.(rowId, localPath)?.catch?.(() => {});
                    }
                    _lastLoaded.delete('server');
                    dispatch(triggerPlaylistRefresh());
                    showAddedCard(playlist.id, newPlaylistName.trim());
                    trackEvent('playlist_created', { track_count: 1, from: 'add_to_playlist_modal' });
                    onHide();
                } else {
                    console.error('Unexpected createPlaylist result:', result);
                    toast.error('Failed to create playlist');
                }
            } else if (isDesktop) {
                const playlist = await window.electron.db.playlists.createFromOnlineTracks(
                    newPlaylistName.trim(),
                    [onlineTrack],
                    newPlaylistDescription.trim() || null
                );

                if (playlist?.id && coverImage) {
                    await window.electron.db.playlists.update(playlist.id, {
                        name: newPlaylistName.trim(),
                        description: newPlaylistDescription.trim() || null,
                        coverImage,
                    });
                }
                if (playlist?.id) {
                    _lastLoaded.delete('local');
                    const allPlaylists = await window.electron.db.playlists.getAll(false);
                    dispatch(setPlaylists(allPlaylists || []));
                    dispatch(triggerPlaylistRefresh());
                    showAddedCard(playlist.id, newPlaylistName.trim());
                    trackEvent('playlist_created', { track_count: 1, from: 'add_to_playlist_modal' });
                    onHide();
                }
            }
        } catch (error) {
            console.error('Error creating playlist:', error);
            toast.error('Failed to create playlist');
        } finally {
            setCreating(false);
        }
    };

    const filteredPlaylists = playlists.filter(p =>
        p.name?.toLowerCase().includes(filter.toLowerCase())
    );

    if (!show) return null;

    return (
        <div className="add-to-playlist-overlay" ref={overlayRef} onClick={handleOverlayClick} onMouseDown={e => e.stopPropagation()}>
            <div className="add-to-playlist-modal">
                <div className="sheet-grabber" aria-hidden="true" />
                <div className="modal-header">
                    <div className="modal-tabs">
                        {activeTab === 'create' && (
                            <button className="modal-back-btn" onClick={() => setActiveTab('add')} aria-label="Back">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                            </button>
                        )}
                        <button
                            className={`modal-tab ${activeTab === 'add' ? 'active' : ''}`}
                            onClick={() => setActiveTab('add')}
                        >
                            Add to playlist
                        </button>
                        <button
                            className={`modal-tab ${activeTab === 'create' ? 'active' : ''}`}
                            onClick={() => setActiveTab('create')}
                        >
                            Create a playlist
                        </button>
                    </div>
                    <button className="modal-close-btn" onClick={onHide}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {activeTab === 'add' && (
                    <div className="modal-body">
                        {(!isMobile || playlists.length > 8) && (isMobileWeb ? (
                            <div className="filter-input-wrapper filter-input-wrapper--mobile">
                                <MobileListSearch
                                    value={filter}
                                    onChange={setFilter}
                                    placeholder="Filter playlists"
                                    ariaLabel="Filter playlists"
                                    overlay={{
                                        fetch: (q) => playlists
                                            .filter(p => matchesQuery(p.name, q))
                                            .map(p => ({
                                                key: `pl-${p.id}`,
                                                title: p.name,
                                                subtitle: `${p.trackCount ?? p.track_count ?? p.tracks?.length ?? 0} tracks`,
                                                artworkUrl: playlistCoverUrl(p),
                                                item: p,
                                            })),
                                        onSelect: (row) => { if (!addingToPlaylistId) handleAddToPlaylist(row.item.id); },
                                    }}
                                />
                            </div>
                        ) : (
                        <div className="filter-input-wrapper">
                            <SearchIcon className="filter-icon" width={16} height={16} />
                            <input
                                type="text"
                                className="filter-input"
                                placeholder="Filter playlists..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                autoFocus={!isMobile}
                            />
                        </div>
                        ))}

                        <div className="playlist-list">
                            {/* Phone: "New playlist" is the first row instead of a second tab. */}
                            <button type="button" className="playlist-row playlist-row--new" onClick={() => setActiveTab('create')}>
                                <div className="playlist-cover">
                                    <div className="playlist-cover-placeholder">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                    </div>
                                </div>
                                <div className="playlist-info">
                                    <span className="playlist-name">New playlist</span>
                                </div>
                            </button>
                            {loading ? (
                                // Ghost rows sized like real ones — the cold
                                // open used to paint a one-line "Loading" then
                                // grow to the full list.
                                [0, 1, 2, 3, 4].map((i) => (
                                    <div key={i} className="playlist-row playlist-skel-row" aria-hidden="true">
                                        <span className="playlist-skel-cover" />
                                        <span className="playlist-skel-line" />
                                    </div>
                                ))
                            ) : filteredPlaylists.length === 0 ? (
                                <div className="playlist-list-empty">
                                    {filter ? 'No playlists match your search' : 'No playlists yet'}
                                </div>
                            ) : (
                                filteredPlaylists.map(playlist => (
                                    <div
                                        key={playlist.id}
                                        className={`playlist-row${isMobile ? ' playlist-row--tappable' : ''}${addingToPlaylistId === playlist.id ? ' is-adding' : ''}`}
                                        role={isMobile ? 'button' : undefined}
                                        onClick={isMobile && !addingToPlaylistId ? () => handleAddToPlaylist(playlist.id) : undefined}
                                    >
                                        <div className="playlist-cover">
                                            {(playlist.cover_image || playlist.tracks?.find(t => t.track?.cover || t.cover)) ? (
                                                <img
                                                    src={playlist.cover_image || playlist.tracks?.find(t => t.track?.cover || t.cover)?.track?.cover || playlist.tracks?.find(t => t.cover)?.cover}
                                                    alt=""
                                                    onError={(e) => { if (e.currentTarget.src !== DEFAULT_PLAYLIST_COVER) e.currentTarget.src = DEFAULT_PLAYLIST_COVER; }}
                                                />
                                            ) : (
                                                <img src={DEFAULT_PLAYLIST_COVER} alt="" />
                                            )}
                                        </div>
                                        <div className="playlist-info">
                                            <span className="playlist-name">{playlist.name}</span>
                                            <span className="playlist-track-count">
                                                {playlist.trackCount} track{playlist.trackCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <button
                                            className="add-to-playlist-btn"
                                            onClick={(e) => { e.stopPropagation(); handleAddToPlaylist(playlist.id); }}
                                            disabled={addingToPlaylistId === playlist.id}
                                        >
                                            {addingToPlaylistId === playlist.id ? 'Adding...' : 'Add to Playlist'}
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'create' && (
                    <div className="modal-body">
                        <form className="create-playlist-form" onSubmit={handleCreatePlaylist}>
                            <div className="form-group">
                                <label htmlFor="playlist-name">Playlist name</label>
                                <input
                                    id="playlist-name"
                                    type="text"
                                    className="form-input"
                                    placeholder="My new playlist"
                                    value={newPlaylistName}
                                    onChange={(e) => setNewPlaylistName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label>Cover (optional)</label>
                                <div className="cover-picker">
                                    <button type="button" className="cover-preview" onClick={() => coverInputRef.current?.click()} title={coverImage ? 'Change cover' : 'Choose cover image'}>
                                        {coverImage ? (
                                            <img src={coverImage} alt="Playlist cover" />
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="cover-actions">
                                        <button type="button" className="cover-choose-btn" onClick={() => coverInputRef.current?.click()}>
                                            {coverImage ? 'Change image' : 'Choose image'}
                                        </button>
                                        {coverImage && (
                                            <button type="button" className="cover-remove-btn" onClick={() => setCoverImage(null)}>
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverFile} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="playlist-description">Description (optional)</label>
                                <textarea
                                    id="playlist-description"
                                    className="form-input form-textarea"
                                    placeholder="Add a description..."
                                    value={newPlaylistDescription}
                                    onChange={(e) => setNewPlaylistDescription(e.target.value)}
                                    rows={3}
                                />
                            </div>
                            <div className="form-track-preview">
                                <span className="form-track-preview-label">{isBulk ? 'Adding:' : 'Track to add:'}</span>
                                <span className="form-track-preview-name">{isBulk ? (bulkLabel || `${bulkFilePaths.length} tracks`) : `${track?.artist} - ${track?.title}`}</span>
                            </div>
                            <button
                                type="submit"
                                className="create-playlist-submit"
                                disabled={creating || !newPlaylistName.trim()}
                            >
                                {creating ? 'Creating...' : 'Create Playlist'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddToPlaylistModal;
